import { logger } from '@/shared/utils';
import { ENV } from '@/config/environment';

interface PerformanceLayoutShift extends PerformanceEntry { value: number }
interface PerformanceEventTiming extends PerformanceEntry { processingStart: number }

export interface PerformanceMetrics {
  totalLoadTime?: number;
  ttfb?: number;
  fcp?: number;
  lcp?: number;
  fid?: number;
  cls?: number;
  custom?: Record<string, number>;
}

export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private enabled: boolean;
  private metrics: PerformanceMetrics = {};
  private marks: Record<string, number> = {};

  constructor() {
    this.enabled = ENV.features.performanceMonitoring;
    this.setupPerformanceObservers();
  }

  public static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  public startMark(name: string): void {
    if (!this.enabled) return;
    this.marks[name] = performance.now();
  }

  public endMark(name: string): number | undefined {
    if (!this.enabled || !this.marks[name]) return;
    const duration = performance.now() - this.marks[name];
    if (!this.metrics.custom) this.metrics.custom = {};
    this.metrics.custom[name] = duration;
    delete this.marks[name];
    return duration;
  }

  public trackComponentRender(componentName: string, startTime: number): void {
    if (!this.enabled) return;
    const renderTime = performance.now() - startTime;
    if (!this.metrics.custom) this.metrics.custom = {};
    this.metrics.custom[`render_${componentName}`] = renderTime;
  }

  private setupPerformanceObservers(): void {
    if (!this.enabled || typeof window === 'undefined' || !('PerformanceObserver' in window)) return;
    try {
      const fcpObserver = new PerformanceObserver((list) => {
        const firstEntry = list.getEntries()[0];
        if (firstEntry) {
          this.metrics.fcp = firstEntry.startTime;
          logger.debug('FCP recorded', 'PERFORMANCE', { fcp: this.metrics.fcp });
        }
      });
      fcpObserver.observe({ type: 'paint', buffered: true });

      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        if (lastEntry) {
          this.metrics.lcp = lastEntry.startTime;
          logger.debug('LCP recorded', 'PERFORMANCE', { lcp: this.metrics.lcp });
        }
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });

      const fidObserver = new PerformanceObserver((list) => {
        const firstEntry = list.getEntries()[0] as PerformanceEventTiming;
        if (firstEntry) {
          this.metrics.fid = firstEntry.processingStart - firstEntry.startTime;
          logger.debug('FID recorded', 'PERFORMANCE', { fid: this.metrics.fid });
        }
      });
      fidObserver.observe({ type: 'first-input', buffered: true });

      let clsValue = 0;
      let clsEntries: PerformanceLayoutShift[] = [];
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries() as PerformanceLayoutShift[];
        clsEntries = clsEntries.concat(entries);
        let sessionValue = 0;
        let sessionEntries: PerformanceLayoutShift[] = [];
        let sessionStart = 0;
        clsEntries.forEach((entry) => {
          if (!sessionStart || entry.startTime - sessionStart > 5000 || sessionEntries.length >= 5) {
            sessionStart = entry.startTime;
            sessionValue = 0;
            sessionEntries = [];
          }
          sessionEntries.push(entry);
          sessionValue += entry.value;
        });
        if (sessionValue > clsValue) {
          clsValue = sessionValue;
          this.metrics.cls = clsValue;
          logger.debug('CLS recorded', 'PERFORMANCE', { cls: this.metrics.cls });
        }
      });
      clsObserver.observe({ type: 'layout-shift', buffered: true });

      const navigationObserver = new PerformanceObserver((list) => {
        const navigationEntry = list.getEntries()[0] as PerformanceNavigationTiming;
        if (navigationEntry) {
          this.metrics.ttfb = navigationEntry.responseStart - navigationEntry.startTime;
          this.metrics.totalLoadTime = navigationEntry.loadEventEnd - navigationEntry.startTime;
          logger.info('Page performance metrics recorded', 'PERFORMANCE', {
            ttfb: this.metrics.ttfb,
            loadTime: this.metrics.totalLoadTime
          });
        }
      });
      navigationObserver.observe({ type: 'navigation', buffered: true });
    } catch (error) {
      logger.error('Error setting up performance observers', 'PerformanceMonitor', { error });
    }
  }

  public getMetrics(): PerformanceMetrics {
    return { ...this.metrics };
  }

}

export function usePerformanceMonitor() {
  return PerformanceMonitor.getInstance();
}

export const performanceMonitor = PerformanceMonitor.getInstance();
